import { useState } from "react";
import { toast } from "sonner";
import { GitMerge, Sparkles, Plus, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AutoTextarea } from "@/components/AutoTextarea";
import { api, errMsg } from "@/lib/api";
import { catColor, catLabel } from "@/lib/lore";

const Opt = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors ${active ? "border-[#D97706] bg-[#D97706]/10 text-[#F3EBDD]" : "border-[#2D3442] text-[#A8A090] hover:text-[#F3EBDD]"}`}>
    {children}
  </button>
);

export function TargetPicker({ extraction, targetId, setTargetId, addAlias, setAddAlias, name }) {
  const exact = extraction.existing_match;
  const cands = extraction.match_candidates || [];
  if (exact) return <p className="flex items-center gap-1.5 text-sm text-[#F59E0B]" data-testid="extraction-merge-notice"><GitMerge className="h-4 w-4" />Adds to the existing entry “{exact.name}”</p>;
  if (!cands.length) return <p className="text-sm text-[#A8A090]" data-testid="extraction-no-match">No existing entry looks like this one: a new entry will be created.</p>;
  const target = cands.find((c) => c.id === targetId);
  return (
    <div className="space-y-2" data-testid="extraction-match-candidates">
      <p className="text-xs text-[#A8A090]">This note may be about an entry you already have.</p>
      {cands.map((c) => (
        <Opt key={c.id} active={targetId === c.id} onClick={() => setTargetId(c.id)} testid={`match-candidate-${c.id}`}>
          <GitMerge className="h-4 w-4 text-[#F59E0B]" />Add to <span className="font-medium text-[#F3EBDD]">{c.name}</span>
          <span className="text-xs" style={{ color: catColor(c.category) }}>{catLabel(c.category)}</span>
          <span className="ml-auto text-xs font-mono-lf">{c.by_ai ? "AI match" : `${c.score}% similar`}</span>
        </Opt>
      ))}
      <Opt active={!targetId} onClick={() => setTargetId(null)} testid="match-candidate-new"><Plus className="h-4 w-4" />Create a new entry “{name}”</Opt>
      {target && target.name.toLowerCase() !== name.trim().toLowerCase() && (
        <label className="flex items-center gap-2 pt-1 text-xs text-[#A8A090]">
          <Checkbox checked={addAlias} onCheckedChange={(v) => setAddAlias(!!v)} data-testid="match-add-alias" />
          Remember “{name}” as another name for {target.name}
        </label>
      )}
    </div>
  );
}

export function MergeModePanel({ targetId, noteText, d, mode, setMode, woven, setWoven }) {
  const [busy, setBusy] = useState(false);
  const runWeave = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/entities/${targetId}/ai/weave`, { note_text: noteText, summary: d.summary, extracted_attributes: d.extracted_attributes });
      setWoven(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3" data-testid="merge-mode-panel">
      <div className="grid grid-cols-2 gap-2">
        <Opt active={mode === "append"} onClick={() => setMode("append")} testid="merge-mode-append"><Plus className="h-4 w-4" />Append note to the end</Opt>
        <Opt active={mode === "weave"} onClick={() => setMode("weave")} testid="merge-mode-weave"><Sparkles className="h-4 w-4 text-[#F59E0B]" />AI weave into the entry</Opt>
      </div>
      {mode === "weave" && !woven && (
        <Button type="button" variant="outline" onClick={runWeave} disabled={busy} data-testid="weave-preview-button">
          <Sparkles className="h-4 w-4 mr-1.5" />{busy ? "Weaving…" : "Preview woven entry"}
        </Button>
      )}
      {mode === "weave" && woven && <WovenEditor woven={woven} setWoven={setWoven} onRedo={runWeave} busy={busy} />}
    </div>
  );
}

function WovenEditor({ woven, setWoven, onRedo, busy }) {
  return (
    <div className="space-y-3 rounded-md border border-[#2D3442] bg-[#0F131A] p-4" data-testid="woven-preview">
      {woven.conflicts?.length > 0 && (
        <div className="rounded border border-[#EF4444]/40 bg-[#EF4444]/5 p-3 text-sm text-[#E8DFD1]" data-testid="woven-conflicts">
          <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#EF4444]"><AlertTriangle className="h-3.5 w-3.5" />Conflicts with the existing entry</div>
          <ul className="list-disc pl-5 space-y-0.5">{woven.conflicts.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </div>
      )}
      <div className="lf-label">Updated summary</div>
      <Textarea rows={3} value={woven.summary} onChange={(e) => setWoven({ ...woven, summary: e.target.value })} data-testid="woven-summary" />
      <div className="lf-label">Updated notes</div>
      <AutoTextarea value={woven.content} onChange={(e) => setWoven({ ...woven, content: e.target.value })} maxVh={50} className="min-h-[200px] leading-relaxed" data-testid="woven-content" />
      <button type="button" onClick={onRedo} disabled={busy} className="text-xs text-[#A8A090] hover:text-[#F59E0B]" data-testid="weave-redo-button">{busy ? "Weaving…" : "Weave again"}</button>
    </div>
  );
}
